import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import GoalButton from "layouts/goals/components/GoalButton";
import goalSelectStyles from "layouts/goals/components/goalSelectStyles";
import {
  EMPLOYEES_MESSAGES,
  EMPLOYMENT_STATUSES,
  EMPLOYEE_TYPES,
  WORK_MODES,
} from "../employees.constants";

const TEXT_FIELDS = [
  "name",
  "display_name",
  "employee_id",
  "job_title",
  "joining_date",
  "probation_end_date",
  "exit_date",
];

const toForm = (employee) => ({
  ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, employee?.[field] || ""])),
  tz: employee?.tz || "",
  employment_status: employee?.employment_status || "active",
  employee_type: employee?.employee_type || "",
  work_mode: employee?.work_mode || "on_site",
});

function FieldLabel({ children }) {
  return (
    <SuiTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
      {children}
    </SuiTypography>
  );
}

function EmployeeFormModal({ open, onClose, onSave, isSubmitting, employee }) {
  const [form, setForm] = useState(toForm(null));
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setForm(toForm(employee));
    setLocalError(null);
  }, [employee, open]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));
  const onInput = (field) => (e) => set(field)(e.target.value);

  // All IANA zones, plus the person's current one if it isn't a canonical name.
  const tzOptions = useMemo(() => {
    const zones = new Set(["UTC", ...Intl.supportedValuesOf("timeZone")]);
    if (employee?.tz) zones.add(employee.tz);
    return Array.from(zones).map((zone) => ({ value: zone, label: zone }));
  }, [employee]);
  const typeOptions = [{ value: "", label: "Not set" }, ...EMPLOYEE_TYPES];

  const datesChanged =
    employee &&
    (form.joining_date !== (employee.joining_date || "") ||
      form.probation_end_date !== (employee.probation_end_date || ""));
  const tzChanged = employee && form.tz !== employee.tz;

  const handleSaveClick = async () => {
    if (!form.name.trim()) return setLocalError(EMPLOYEES_MESSAGES.nameRequired);
    if (!form.tz) return setLocalError(EMPLOYEES_MESSAGES.tzRequired);
    if (
      form.joining_date &&
      form.probation_end_date &&
      form.probation_end_date < form.joining_date
    ) {
      return setLocalError(EMPLOYEES_MESSAGES.probationBeforeJoining);
    }
    if (form.joining_date && form.exit_date && form.exit_date < form.joining_date) {
      return setLocalError(EMPLOYEES_MESSAGES.exitBeforeJoining);
    }
    setLocalError(null);
    await onSave(form);
  };

  const textField = (field, label, props = {}) => (
    <Grid item xs={12} sm={6}>
      <FieldLabel>{label}</FieldLabel>
      <SuiInput
        name={`employee-${field}`}
        autoComplete="off"
        value={form[field]}
        onChange={onInput(field)}
        {...props}
      />
    </Grid>
  );

  const selectField = (field, label, options, props = {}) => (
    <Grid item xs={12} sm={6}>
      <FieldLabel>{label}</FieldLabel>
      <CustomSelect
        options={options}
        value={options.find((option) => option.value === form[field]) || null}
        onChange={(option) => set(field)(option.value)}
        customStyles={goalSelectStyles}
        menuPortalTarget={document.body}
        {...props}
      />
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { overflow: "visible" } }}
    >
      <DialogTitle>Edit Employee</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        {localError && (
          <SuiTypography variant="caption" color="error" display="block" mb={1}>
            {localError}
          </SuiTypography>
        )}
        <Grid container spacing={2}>
          {textField("name", "Name", { placeholder: "Full name" })}
          {textField("display_name", "Display name")}
          {textField("employee_id", "Employee ID", { placeholder: "e.g. PB-0042" })}
          {textField("job_title", "Job title")}
          {selectField("employee_type", "Employee type", typeOptions)}
          {selectField("work_mode", "Work mode", WORK_MODES)}
          {selectField("tz", "Timezone", tzOptions, { isSearchable: true })}
          {selectField("employment_status", "Status", EMPLOYMENT_STATUSES)}
          {textField("joining_date", "Joining date", { type: "date" })}
          {textField("probation_end_date", "Probation end date", { type: "date" })}
          {textField("exit_date", "Exit date", { type: "date" })}
        </Grid>
        <SuiBox mt={2}>
          {datesChanged && (
            <SuiTypography variant="caption" color="warning" display="block">
              {EMPLOYEES_MESSAGES.datesWarning}
            </SuiTypography>
          )}
          {tzChanged && (
            <SuiTypography variant="caption" color="warning" display="block">
              {EMPLOYEES_MESSAGES.tzWarning}
            </SuiTypography>
          )}
        </SuiBox>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </GoalButton>
        <GoalButton variant="primary" icon="save" loading={isSubmitting} onClick={handleSaveClick}>
          Save changes
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default EmployeeFormModal;
